import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, useTheme } from '@Sage/hoi-poi-ui';

import AudioVisualizer from 'components/AudioVisualizer';
import useAudioEvents from 'hooks/useAudioEvents';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotVoiceRecording');

const VoiceRecording = memo(
    ({
        onError,
        onRecordingComplete,
        shouldResume = false,
        stopRecordingRef,
        targetRate = 44100,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useStyles();
        const { t } = useWeb5Context();
        const theme = useTheme();

        // ========================================
        // LOCAL STATE
        // ========================================
        const [recordedBatches, setRecordedBatches] = useState(null);

        // ========================================
        // REFS
        // ========================================
        const initWaveformRef = useRef(false);
        const hasNotifiedParentRef = useRef(false);
        const stopPlaybackRef = useRef(null);
        const hasStartedRef = useRef(false);
        const previousShouldResumeRef = useRef(shouldResume);
        const isActionInProgressRef = useRef(false);
        const resumeRef = useRef(null);
        const startRef = useRef(null);
        const isInitialMountRef = useRef(true);

        // ========================================
        // AUDIO HOOKS
        // ========================================
        const {
            audioBufferDuration,
            audioDuration,
            bindCanvas,
            error,
            getCompressedAudio,
            handleTimeUpdate,
            initializeWaveform,
            isPlaying,
            level,
            playback,
            playbackContext,
            playbackStartTime,
            resume,
            seek,
            start,
            status,
            stop,
            stopPlayback,
            supportedAudioFormat,
            waveformData,
        } = useAudioEvents({
            colors: {
                centerLine: theme.colors.actionMinor[100],
                waveform: theme.colors.actionMajor[150],
                waveformPlayed: theme.colors.actionMajor[500],
            },
            onRecordingComplete: (batches) => {
                setRecordedBatches(batches);
            },
            targetRate,
        });

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const isRecording = useMemo(() => status === 'recording', [status]);
        const isStarting = useMemo(() => status === 'starting', [status]);
        const isStopped = useMemo(
            () => status === 'idle' && recordedBatches !== null,
            [status, recordedBatches],
        );

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handlePlayPause = useCallback(() => {
            if (!recordedBatches) return;

            if (isPlaying) {
                stopPlayback();
            } else {
                playback(recordedBatches);
            }
        }, [isPlaying, playback, recordedBatches, stopPlayback]);

        const handleSeek = useCallback(
            (targetTime, resumePlayback) => {
                seek(targetTime, resumePlayback);
            },
            [seek],
        );

        // ========================================
        // EFFECTS
        // ========================================
        // Keep function refs up to date
        useEffect(() => {
            stopPlaybackRef.current = stopPlayback;
            resumeRef.current = resume;
            startRef.current = start;
        }, [resume, start, stopPlayback]);

        useEffect(() => {
            // Expose stop function to parent via ref
            if (stopRecordingRef) {
                stopRecordingRef.current = stop;
            }

            return () => {
                if (stopRecordingRef) {
                    stopRecordingRef.current = null;
                }
            };
        }, [stop, stopRecordingRef]);

        // Initialize waveform when recording stops
        useEffect(() => {
            if (recordedBatches && recordedBatches.length > 0 && !initWaveformRef.current) {
                initializeWaveform(recordedBatches);
                initWaveformRef.current = true;
            }
        }, [initializeWaveform, recordedBatches]);

        // Notify parent when waveform is initialized with correct duration
        useEffect(() => {
            if (
                recordedBatches &&
                recordedBatches.length > 0 &&
                initWaveformRef.current &&
                waveformData &&
                audioDuration > 0 &&
                !hasNotifiedParentRef.current
            ) {
                // Mark as notified immediately to prevent duplicate calls
                hasNotifiedParentRef.current = true;

                // Calculate duration from batches to ensure accuracy
                // This matches what initializeWaveform does internally
                const sampleCount = recordedBatches.reduce((sum, batch) => {
                    const binary = atob(batch.audio);
                    return sum + binary.length / 2; // 2 bytes per sample (PCM16)
                }, 0);
                const calculatedDuration = sampleCount / targetRate;

                // Get compressed audio and notify parent
                const notifyParent = async () => {
                    let compressedAudio = null;
                    let audioFormat = null;

                    // Try to get compressed audio if format is supported
                    if (supportedAudioFormat) {
                        try {
                            const compressedResult = await getCompressedAudio();
                            if (compressedResult) {
                                compressedAudio = compressedResult.base64;
                                audioFormat = compressedResult.format;
                            }
                        } catch (err) {
                            console.warn('Failed to compress audio, falling back to PCM16:', err);
                        }
                    }

                    // Notify parent when recording completes with full audio data
                    if (onRecordingComplete) {
                        onRecordingComplete({
                            audioDuration: calculatedDuration,
                            audioFormat, // 'm4a', 'webm', or null (fallback to pcm16)
                            batches: recordedBatches,
                            compressedAudio, // base64 encoded compressed audio or null
                            waveformData,
                        });
                    }
                };

                notifyParent();
            }
        }, [
            audioDuration,
            getCompressedAudio,
            onRecordingComplete,
            recordedBatches,
            supportedAudioFormat,
            targetRate,
            waveformData,
        ]);

        // Notify parent when error occurs
        useEffect(() => {
            if (error && onError) {
                onError(error);
            }
        }, [error, onError]);

        // Effect to handle initial start or resume - only trigger on shouldResume or status changes
        useEffect(() => {
            // Don't do anything if already recording, starting, or stopping
            if (status === 'recording' || status === 'starting' || status === 'stopping') {
                // Reset action in progress flag when we're actively recording
                if (status === 'recording') {
                    isActionInProgressRef.current = false;
                }
                return;
            }

            // Only act when we're idle
            if (status === 'idle') {
                // Check if shouldResume changed from false to true (resuming)
                const isResuming = shouldResume && !previousShouldResumeRef.current;

                // Check if shouldResume changed from true to false (pausing)
                const isPausing = !shouldResume && previousShouldResumeRef.current;

                // Update previous value AFTER checking
                const prevShouldResume = previousShouldResumeRef.current;
                previousShouldResumeRef.current = shouldResume;

                // If pausing, just reset hasStartedRef - stop() was already called by parent
                if (isPausing) {
                    hasStartedRef.current = false;
                    isActionInProgressRef.current = false;
                    return;
                }

                // Only start/resume if we haven't already initiated an action and there's a meaningful change
                if (!isActionInProgressRef.current) {
                    // If shouldResume changed to true, we're resuming a paused recording
                    if (isResuming) {
                        // Reset waveform initialization flag and recorded batches when resuming
                        // so waveform can be initialized again when recording stops
                        initWaveformRef.current = false;
                        hasNotifiedParentRef.current = false;
                        setRecordedBatches(null);
                        isActionInProgressRef.current = true;
                        resumeRef.current();
                        hasStartedRef.current = true;
                        isInitialMountRef.current = false;
                    }
                    // If this is the initial mount and shouldResume is false, start recording
                    else if (isInitialMountRef.current && !shouldResume && !hasStartedRef.current) {
                        // Only start on initial mount
                        hasNotifiedParentRef.current = false;
                        isActionInProgressRef.current = true;
                        startRef.current();
                        hasStartedRef.current = true;
                        isInitialMountRef.current = false;
                    }
                }
            }

            // Cleanup on unmount
            return () => {
                stopPlaybackRef.current();
                hasStartedRef.current = false;
                isActionInProgressRef.current = false;
            };
        }, [shouldResume, status]); // Only depend on shouldResume and status, use refs for functions

        // ========================================
        // RENDER
        // ========================================

        return (
            <div className={classes.root}>
                <div className={classes.visualizerContainer}>
                    <AudioVisualizer
                        audioBufferDuration={audioBufferDuration}
                        audioDuration={audioDuration}
                        bindCanvas={bindCanvas}
                        height={24}
                        isActive={isRecording || isPlaying}
                        isPlaybackMode={isStopped}
                        isPlaying={isPlaying}
                        level={level}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onTimeUpdate={handleTimeUpdate}
                        playbackContext={playbackContext}
                        playbackStartTime={playbackStartTime}
                    />
                </div>
            </div>
        );
    },
);

VoiceRecording.displayName = 'VoiceRecording';

VoiceRecording.propTypes = {
    onError: PropTypes.func,
    onRecordingComplete: PropTypes.func,
    shouldResume: PropTypes.bool,
    stopRecordingRef: PropTypes.shape({ current: PropTypes.func }),
    targetRate: PropTypes.number,
};

export default VoiceRecording;
